import { useFlags } from "launchdarkly-react-client-sdk";

import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { AMBIENT_SCRIBE_CUSTOM_TEMPLATES } from "constants/feature-flags";
import FieldLabel from "features/staff-tasks-v2/drawer/staff-task-card/field-label.component";
import { useGetAmbientScribeTemplatesQuery } from "services/ambient-scribe";
import type { AmbientScribeTemplate } from "services/types/ambient-scribe-template.type";

import {
  AppointmentTypeDot,
  ScribeTemplateTypeLabel
} from "./scribe-template-type-label.component";
import type { LinkedAppointmentType } from "./use-scribe-template-appointment-types.hook";

export const AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS = {
  limit: 100,
  offset: 0,
  ordering: "-pk"
};

/**
 * Label for the backend's built-in SOAP prompt on the two chart-note surfaces that offer it as a
 * real choice (the chart-note provenance alert and the scribe dialog's SOAP tab).
 *
 * Named "Suno …" on purpose: `is_default` is a separate concept — a template the practice marked
 * as its default — and an unqualified "Default SOAP Template" reads as that one. Mirrors the
 * audiology picker's `"Suno default summary"`.
 */
export const BUILT_IN_CHART_NOTE_TEMPLATE_LABEL = "Suno Default SOAP Template";

/** MUI's empty `Select` value. Means "built-in prompt" when `builtInOptionLabel` is set. */
const EMPTY_SELECT_VALUE = "";

// A template can be the default for many appointment types; past two, collapse so one
// option can't grow tall enough to push the rest of the list off screen.
const MAX_VISIBLE_APPOINTMENT_TYPES = 2;

type AmbientScribeTemplateSelectProps = {
  /**
   * Three states, not two — see `builtInOptionLabel`. An id renders that template; `null` means
   * the built-in prompt is selected; `undefined` means nothing is selected, or the caller is
   * holding an id it could not resolve to a template in the list.
   */
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  showLabel?: boolean;
  label?: string;
  /** When provided, skip the internal templates fetch (caller owns the list). */
  templates?: AmbientScribeTemplate[];
  /**
   * Renders the template type and its appointment types under each option name
   * (SN-19067). Off by default so the pre-flag option markup is unchanged.
   */
  showTemplateDetails?: boolean;
  appointmentTypesByTemplateId?: Map<number, LinkedAppointmentType[]>;
  sx?: object;
  /**
   * Overrides the control's default 280–400px width. Needed by callers that sit the picker
   * inline inside a sentence, where the default is far too wide.
   */
  formControlSx?: object;
  /**
   * Typography variant for the CLOSED control's text (selected name / placeholder). Defaults
   * to `body3` — 18px, which is larger than `body2` body copy, so an inline caller sitting
   * the picker inside a sentence wants something smaller. Does not affect the menu options.
   */
  valueVariant?: "body1" | "body2" | "body3" | "caption";
  /**
   * Opt in to a **selectable** "no template" option carrying this label, for callers whose
   * endpoint treats a missing template id as "use the built-in prompt" rather than as an error.
   *
   * Setting it flips what `value: null` means — "the built-in prompt is selected", a real choice
   * that renders in the selected style — so `undefined` becomes the only way to say
   * "nothing / unknown". Pass `undefined` for an id you could not resolve, or a soft-deleted
   * template gets misreported as the built-in prompt.
   *
   * Omitted => unchanged for every existing caller: the empty option stays a disabled
   * placeholder and `onChange` never emits `null`.
   */
  builtInOptionLabel?: string;
};

export default function AmbientScribeTemplateSelect({
  value,
  onChange,
  disabled = false,
  showLabel = false,
  label = "Scribe Template",
  templates: templatesProp,
  showTemplateDetails = false,
  appointmentTypesByTemplateId,
  sx,
  formControlSx,
  valueVariant = "body3",
  builtInOptionLabel
}: Readonly<AmbientScribeTemplateSelectProps>) {
  const flags = useFlags();
  const isAmbientScribeCustomTemplatesEnabled =
    flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES];

  const { data: fetchedTemplates = [] } = useGetAmbientScribeTemplatesQuery(
    AMBIENT_SCRIBE_TEMPLATES_QUERY_PARAMS,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      skip: !isAmbientScribeCustomTemplatesEnabled || templatesProp != null
    }
  );

  const templates = templatesProp ?? fetchedTemplates;
  const hasTemplates = templates.length > 0;

  if (!isAmbientScribeCustomTemplatesEnabled || !hasTemplates) {
    return null;
  }

  const resolvedValue =
    value != null && templates.some((t) => t.id === value)
      ? value
      : EMPTY_SELECT_VALUE;

  const canPickBuiltIn = builtInOptionLabel != null;
  // `null` only reads as "the built-in prompt" for a caller that offers it; for everyone else it
  // still means "nothing selected", as it always has.
  const isBuiltInSelected = canPickBuiltIn && value === null;

  // What the CLOSED control says, resolved in one place so the three value states cannot drift
  // apart. `null` here means "show the muted placeholder".
  const selectedLabel = resolvedValue
    ? (templates.find((t) => Number(t.id) === Number(resolvedValue))?.name ??
      "")
    : isBuiltInSelected
      ? builtInOptionLabel
      : null;

  // Deliberately NOT the same string as the menu row below — the closed control has always said
  // "Select Scribe Template" where the row says "Select a template". Preserved verbatim.
  const emptyOptionText = canPickBuiltIn
    ? builtInOptionLabel
    : showTemplateDetails
      ? "Choose a template"
      : "Select a template";

  return (
    <Stack sx={sx}>
      {showLabel && <FieldLabel label={label} sx={{ mb: "3px" }} />}
      <FormControl
        size="small"
        sx={{ minWidth: 280, maxWidth: 400, ...formControlSx }}
        disabled={disabled}
      >
        <Select
          id="scribe-template-select"
          value={resolvedValue}
          displayEmpty
          sx={{
            "& .MuiOutlinedInput-notchedOutline legend": {
              display: "none"
            },
            "& .MuiOutlinedInput-notchedOutline": { top: 0 }
          }}
          // Stays a single line even when the options are three-high, so the closed
          // control keeps its height.
          renderValue={() =>
            selectedLabel != null ? (
              // The built-in prompt is a real selection, so it shares the selected styling —
              // only a genuinely empty value gets the muted placeholder.
              <Typography
                variant={valueVariant}
                component="span"
                fontWeight={700}
                noWrap
              >
                {selectedLabel}
              </Typography>
            ) : (
              <Typography
                variant={valueVariant}
                color="text.secondary"
                component="span"
              >
                {showTemplateDetails
                  ? "Choose a template"
                  : "Select Scribe Template"}
              </Typography>
            )
          }
          onChange={(e) => {
            const next = e.target.value;
            // Branch on the empty value BEFORE any Number() conversion: `Number("")` is `0`, not
            // `null`, and a leaked `0` reads as a real template id downstream — POST /{id}/async/
            // only strips *nullish* keys, so it would be sent as `ambient_scribe_template_id: 0`.
            if (next === EMPTY_SELECT_VALUE) {
              if (canPickBuiltIn) {
                onChange(null);
              }
              return;
            }
            onChange(Number(next));
          }}
          MenuProps={{
            sx: { zIndex: 9999 },
            PaperProps: {
              sx: {
                zIndex: 9999,
                ...(showTemplateDetails
                  ? { maxHeight: 360, minWidth: 320 }
                  : {})
              }
            }
          }}
          disabled={disabled}
        >
          {/* Selectable only for a caller that opted in — "no template" is a real choice there.
              No type sub-label: this is the backend's built-in prompt, not a template. */}
          <MenuItem
            value={EMPTY_SELECT_VALUE}
            disabled={!canPickBuiltIn}
            sx={
              canPickBuiltIn && showTemplateDetails
                ? {
                    py: 1,
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`
                  }
                : undefined
            }
          >
            {emptyOptionText}
          </MenuItem>
          {templates.map((template) => {
            const appointmentTypes =
              appointmentTypesByTemplateId?.get(template.id) ?? [];
            return (
              <MenuItem
                key={template.id}
                value={template.id}
                sx={
                  showTemplateDetails
                    ? {
                        alignItems: "flex-start",
                        py: 1,
                        whiteSpace: "normal",
                        "&:not(:last-of-type)": {
                          borderBottom: (theme) =>
                            `1px solid ${theme.palette.divider}`
                        }
                      }
                    : undefined
                }
              >
                {showTemplateDetails ? (
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography variant="body3" fontWeight={700}>
                      {template.name}
                    </Typography>
                    <ScribeTemplateTypeLabel
                      type={template.type}
                      variant="caption"
                      muted
                    />
                    {/* Omitted entirely rather than rendering an empty row. */}
                    {appointmentTypes.length > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                        rowGap={0.25}
                        flexWrap="wrap"
                        sx={{ minWidth: 0 }}
                      >
                        {appointmentTypes
                          .slice(0, MAX_VISIBLE_APPOINTMENT_TYPES)
                          .map((appointmentType) => (
                            <Stack
                              key={appointmentType.id}
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <AppointmentTypeDot
                                color={appointmentType.color}
                                size={10}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {appointmentType.name}
                              </Typography>
                            </Stack>
                          ))}
                        {appointmentTypes.length >
                          MAX_VISIBLE_APPOINTMENT_TYPES && (
                          <Typography variant="caption" color="text.secondary">
                            +
                            {appointmentTypes.length -
                              MAX_VISIBLE_APPOINTMENT_TYPES}{" "}
                            more
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                ) : (
                  template.name
                )}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Stack>
  );
}
